import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import axios from 'axios'
import { toast } from 'sonner'

import esMX from '../i18n/es-MX.json'
import enUS from '../i18n/en-US.json'

const LanguageContext = createContext(null)

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

const dictionaries = {
  'es-MX': esMX,
  'en-US': enUS
}

// Detect browser language
const detectBrowserLocale = () => {
  const browserLang = navigator.language || navigator.userLanguage || 'en-US'
  return browserLang.startsWith('es') ? 'es-MX' : 'en-US'
}

// Get nested value from object by dot notation
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj)
}

export const LanguageProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  // Initialize locale on mount
  useEffect(() => {
    const initLocale = async () => {
      const token = localStorage.getItem('token')
      
      if (token) {
        // User is logged in, fetch locale from backend
        setIsAuthenticated(true)
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
          const response = await axios.get(`${API}/auth/me`)
          const userLocale = response.data.locale || 'es-MX'
          setLocaleState(userLocale)
        } catch (error) {
          // Token might be invalid, fall back to browser detection
          console.error('Error fetching user locale:', error)
          setLocaleState(detectBrowserLocale())
        }
      } else {
        // Not logged in, use browser detection
        setIsAuthenticated(false)
        setLocaleState(detectBrowserLocale())
      }
      
      setIsInitialized(true)
    }

    initLocale()
  }, [])

  // Listen for auth changes (login/logout)
  useEffect(() => {
    const handleStorageChange = () => {
      const token = localStorage.getItem('token')
      setIsAuthenticated(!!token)
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  // Set locale with optional backend persistence
  const setLocale = useCallback(async (newLocale, persistToBackend = true) => {
    if (!['es-MX', 'en-US'].includes(newLocale)) {
      console.warn(`Invalid locale: ${newLocale}`)
      return
    }

    const previousLocale = locale
    
    // Update UI immediately
    setLocaleState(newLocale)

    // Persist to backend if authenticated
    if (persistToBackend && isAuthenticated && localStorage.getItem('token')) {
      try {
        await axios.put(`${API}/auth/settings`, { 
          locale: newLocale,
          daily_goal: 20, // Keep existing settings
          theme: 'system'
        })
      } catch (error) {
        console.error('Error persisting locale:', error)
        // Revert on error
        setLocaleState(previousLocale)
        toast.error(locale === 'es-MX' 
          ? 'Error al guardar el idioma' 
          : 'Error saving language preference'
        )
      }
    }
  }, [locale, isAuthenticated])

  // Translation function with parameter interpolation
  const t = useCallback((key, params = {}) => {
    if (!locale) return key

    // Try current locale first
    let value = getNestedValue(dictionaries[locale], key)
    
    // Fallback to en-US if key not found
    if (value === undefined && locale !== 'en-US') {
      value = getNestedValue(dictionaries['en-US'], key)
    }
    
    // Return key if still not found
    if (value === undefined) {
      console.warn(`Translation key not found: ${key}`)
      return key
    }

    // Interpolate parameters like {name}, {count}, etc.
    if (params && typeof value === 'string') {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        value = value.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), paramValue)
      })
    }

    return value
  }, [locale])

  // Format date according to locale
  const formatDate = useCallback((date, options = {}) => {
    if (!date || !locale) return ''
    
    const dateObj = typeof date === 'string' ? new Date(date) : date
    const defaultOptions = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      ...options 
    }
    
    try {
      return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj)
    } catch (error) {
      console.error('Error formatting date:', error)
      return date.toString()
    }
  }, [locale])

  // Format number according to locale
  const formatNumber = useCallback((number, options = {}) => {
    if (number === undefined || number === null || !locale) return ''
    
    const defaultOptions = {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      ...options
    }
    
    try {
      return new Intl.NumberFormat(locale, defaultOptions).format(number)
    } catch (error) {
      console.error('Error formatting number:', error)
      return number.toString()
    }
  }, [locale])

  // Format currency according to locale
  const formatCurrency = useCallback((amount, currency = 'MXN', options = {}) => {
    if (amount === undefined || amount === null || !locale) return ''
    
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
        ...options
      }).format(amount)
    } catch (error) {
      console.error('Error formatting currency:', error)
      return `${currency} ${amount}`
    }
  }, [locale])

  // Update authentication state (called from AuthContext)
  const updateAuthState = useCallback((authenticated, userLocale = null) => {
    setIsAuthenticated(authenticated)
    if (userLocale) {
      setLocaleState(userLocale)
    }
  }, [])

  const value = useMemo(() => ({
    locale,
    setLocale,
    t,
    formatDate,
    formatNumber,
    formatCurrency,
    isInitialized,
    updateAuthState
  }), [locale, setLocale, t, formatDate, formatNumber, formatCurrency, isInitialized, updateAuthState])

  // Show loading spinner until locale is initialized
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="text-muted-foreground text-sm">KaizenOS</span>
        </div>
      </div>
    )
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}
